import React from "react";
import { Card, Col, Icon, Row, Statistic, Tooltip } from "antd";
import styles from "./style.module.scss";
import { StatisticsType } from "../../pages/advancedDashboard";
import CONST from "../../utilities/constant";

const DashboardOverView = (props: { statistics: StatisticsType }) => {
  const {
    group_ids,
    total_members,
    total_active_members,
    active_30_days_users,
    total_training_sessions,
    weekly_avg,
    feed_forward_habit_index_avg,
    feed_forward_last90days_habit_index_avg,
    feed_forward_trust_nps,
    feed_forward_last90days_trust_nps_avg,
    feed_forward_last90days_total_records,
    effect_survey,
    total_training_activities,
    total_weekly_reflections,
    total_feed_forwards,
    completed_feed_forwards,
  } = props.statistics;

  const activatedRatio = total_members
    ? `(${((total_active_members / total_members) * 100).toFixed()}%)`
    : "";

  const days30Ratio = total_members
    ? `(${((active_30_days_users / total_active_members) * 100).toFixed()}%)`
    : "";
  const total_feed_forwardsRatio = total_feed_forwards ? `(${total_feed_forwards})` : "";
  const positiveAnswer = effect_survey.reduce((pre, cur) => pre + cur.no_of_yes, 0);
  const negativeAnswer = effect_survey.reduce((pre, cur) => pre + cur.no_of_no, 0);
  const sum = positiveAnswer + negativeAnswer;
  const improvementScore = sum < 3 ? "--" : ((positiveAnswer / sum) * 100).toFixed(1);
  const habitIndexValue = feed_forward_last90days_habit_index_avg || feed_forward_habit_index_avg;
  const trustNPSValue = feed_forward_last90days_trust_nps_avg || feed_forward_trust_nps;

  return (
    <div>
      <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
        <Col span={6} offset={6}>
          <Card>
            <Tooltip
              placement="left"
              title="The Habit Index is a score between 0 to 100. A high score means that your leaders have many habits and do behaviors frequently enough for their team members. The score is based on the number of leaders who got their results within the last 90 days."
            >
              <div>
                <Statistic
                  title="Habit Index"
                  value={habitIndexValue ? Math.round(habitIndexValue) : "N/A"}
                  prefix={<Icon type="dashboard" />}
                />
                {!!feed_forward_last90days_total_records && (
                  <div className={styles.footer_box}>
                    {`Based on ${feed_forward_last90days_total_records} members, last 90 days`}
                  </div>
                )}
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="right"
              title='The Trust NPS is a score between -100 to +100. The question is "how likely are you to recommend your leader as someone who you trust?" The score is based on the number of leaders who got their results within the last 90 days.'
            >
              <div>
                <Statistic
                  title="Trust NPS"
                  value={trustNPSValue ? Math.round(trustNPSValue) : "N/A"}
                  prefix={<Icon type="like" />}
                />
                {!!feed_forward_last90days_total_records && (
                  <div className={styles.footer_box}>
                    {`Based on ${feed_forward_last90days_total_records} members, last 90 days`}
                  </div>
                )}
              </div>
            </Tooltip>
          </Card>
        </Col>
      </Row>

      <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
        <Col span={6} offset={6}>
          <Card>
            <Tooltip
              placement="left"
              title="Average number of training activities completed per week the last 30 days."
            >
              <div>
                <Statistic
                  title="Weekly average last 30 days"
                  value={weekly_avg.toFixed(2)}
                  prefix={<Icon type="flag" />}
                  suffix="/ week"
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="right"
              title="Overall average, when asking improvement questions during training."
            >
              <div>
                <Statistic
                  title="Improvement score"
                  value={improvementScore}
                  prefix={<Icon type="rise" />}
                  suffix="%"
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
      </Row>
      <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
        <Col span={6}>
          <Card>
            <Tooltip placement="topLeft" title="Total amount of members you have added!">
              <div>
                <Statistic
                  title="Total members"
                  value={total_members}
                  prefix={<Icon type="user" />}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="How many of your leaders who have logged in and activated their account. Showing percentage (%) of total members in brackets."
            >
              <div>
                <Statistic
                  title="Activated members"
                  value={total_active_members}
                  suffix={activatedRatio}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="How many members that have shown activity in the app last 30 days. Showing percentage (%) of total members in brackets."
            >
              <div>
                <Statistic
                  title="Active last 30 days"
                  prefix={<Icon type="schedule" />}
                  value={active_30_days_users}
                  suffix={days30Ratio}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="Total amount of groups you have added on this company account. To see data for each group, go to Groups in the left hand menu."
            >
              <div>
                <Statistic
                  title="Groups"
                  value={group_ids.length}
                  prefix={<Icon type="solution" />}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
      </Row>
      <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="Total completed activities in the app; includes all training sessions, weekly reflections and Leadership Habit Profiles."
            >
              <div>
                <Statistic
                  title="Total training activities"
                  value={total_training_activities}
                  prefix={<Icon type="appstore" />}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip placement="topLeft" title="All completed training activities ">
              <div>
                <Statistic
                  title="Total training sessions"
                  value={total_training_sessions}
                  prefix={<Icon type="safety-certificate" />}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="The total number of times your leaders have reflected on their week."
            >
              <div>
                <Statistic
                  title="Weekly reflections"
                  value={total_weekly_reflections}
                  prefix={<Icon type="coffee" />}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Tooltip
              placement="topLeft"
              title="Showing how many Leadership Habit Profiles that have been initiated, and how many that has been completed. Example: if it says 70 (80) it means 80 have initiated LHP and 70 leaders have completed."
            >
              <div>
                <Statistic
                  title="Habit Profiles"
                  value={completed_feed_forwards}
                  prefix={<Icon type="team" />}
                  suffix={total_feed_forwardsRatio}
                />
              </div>
            </Tooltip>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardOverView;
